import { useLocation, useNavigate } from '@tanstack/react-router';
import { Announcement03, Check, ChevronDown, Plus } from '@untitled-ui/icons-react';

import { type Brand } from '../entity/api/brand';
import { type Organization } from '../entity/api/organization';
import { trackError } from '../error/tracking';
import { captureException } from '../logger/capture-exception';
import { useUpdateUser } from '../services/storage';
import { tNamespace } from '../services/translation';
import { setSelectedBrand } from '../shared/brand';
import { capitalize } from '../shared/string';
import { AppDropdownBody, DropdownLabel, DropdownMenu, DropdownSeparator } from './dropdown-menu';
import { DropdownMenuOption, MenuOptionIcon } from './menu-option';
import { Skeleton } from './skeleton';

const t = tNamespace('components.brands-nav');

export type BrandsNavProps = {
  brands: Brand[] | undefined;
  currentBrand: Brand | undefined | null;
  currentOrganization: Organization | undefined | null;
  isDisabled: boolean;
};

export function BrandsNav(props: BrandsNavProps) {
  const { brands, currentBrand, isDisabled } = props;
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const updateUserMutation = useUpdateUser();

  if (brands == null || brands.length === 0 || currentBrand == null) {
    return (
      <div className="h-16 w-full rounded-lg border border-branding-gray bg-white px-4 py-3">
        <Skeleton className="inline-block h-4 w-[40%] rounded-full" />
      </div>
    );
  }
  return (
    <DropdownMenu
      closeOnSelect
      position="start"
      trigger={
        <div className="relative flex h-[72px] w-full flex-col items-start justify-center rounded-lg border border-branding-gray bg-white p-4 text-left outline-none hover:bg-branding-light-gray focus:outline-none group-data-[state=open]:bg-branding-gray">
          <p
            data-testid="brandsnav-dropdown-brand"
            className="font-body-2-m max-w-[180px] truncate"
          >
            {capitalize(currentBrand.name)}
          </p>
          {currentBrand.companyWebsite.length > 0 ? (
            <p className="font-body-2-r max-w-[180px] truncate text-branding-gray">
              {currentBrand.companyWebsite}
            </p>
          ) : null}
          <ChevronDown
            className="absolute right-4 top-1/2 -translate-y-1/2 text-black"
            width={16}
            height={16}
          />
        </div>
      }
      disabled={isDisabled}
      triggerClassName="w-full mb-6"
    >
      <AppDropdownBody>
        <DropdownLabel>{t('brands')}</DropdownLabel>
        <div className="max-h-60 overflow-y-auto">
          {brands.map(function (brand, index) {
            const isActive = brand._id === currentBrand._id;
            return (
              <DropdownMenuOption
                key={index}
                mainText={brand.name}
                isActive={isActive}
                className="mb-1 cursor-pointer"
                leftElement={
                  <Announcement03
                    width={16}
                    height={16}
                    className={
                      isActive
                        ? 'text-branding-accent-600'
                        : 'text-black group-hover:text-branding-accent-600 group-focus:text-branding-accent-600'
                    }
                  />
                }
                rightElement={
                  isActive ? (
                    <Check width={16} height={16} className="text-branding-accent-600" />
                  ) : null
                }
                onClick={async function () {
                  let brandToUpd = brand._id;

                  try {
                    if (pathname.includes('/app/brand-context')) {
                      await navigate({
                        to: '/app/brand-context',
                        search: { brandId: brandToUpd },
                      });

                      // NOTE: there is a case with unsaved changes where the brand might end up
                      //       being different and the selected dropdown becomes different
                      //       from what is being shown. since the tanstack router block works
                      //       on top of the location, we can then check specifically
                      //       on the location href for the brand id that is active
                      const hrefBrandId = new URL(window.location.href).searchParams.get('brandId');
                      brandToUpd = hrefBrandId ?? brandToUpd;
                    }

                    await setSelectedBrand(brandToUpd);

                    updateUserMutation.mutate(
                      { selectedBrandId: brandToUpd },
                      {
                        onError: function (err) {
                          captureException(
                            err,
                            {
                              file: 'components/brands-nav.tsx',
                              method: 'onClick',
                              infoMessage: 'unable to persist selected brand to server',
                            },
                            trackError
                          );
                        },
                      }
                    );
                  } catch (err) {
                    captureException(
                      err,
                      {
                        file: 'components/brands-nav.tsx',
                        method: 'onClick',
                        infoMessage: 'unable to set selected brand',
                      },
                      trackError
                    );
                  }
                }}
              />
            );
          })}
        </div>
        <DropdownSeparator />
        {props.currentOrganization == null ? null : (
          <DropdownMenuOption
            mainText={t('addBrand')}
            variant="gray"
            leftElement={
              <MenuOptionIcon variant="gray">
                <Plus width={16} height={16} />
              </MenuOptionIcon>
            }
            onClick={function () {
              navigate({
                to: `/app/settings/organization/${props.currentOrganization?._id}/brands`,
                params: { id: props.currentOrganization?._id ?? '' },
                search: {
                  addNewBrand: true,
                },
              }).catch(function (err) {
                captureException(
                  err,
                  {
                    file: 'components/brands-nav.tsx',
                    method: 'navigate',
                    infoMessage: `unable to navigate to /app/settings/organization/${props.currentOrganization?._id}/brands`,
                  },
                  trackError
                );
              });
            }}
          />
        )}
      </AppDropdownBody>
    </DropdownMenu>
  );
}
